const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

/**
 * The delivery address of an order, read the way the carrier receives it (Facade
 * FulfillmentOrderV1ShapeMapper.toShippingAddress). A web order holds it as `shipping.shippingAddress`; the marketing
 * import writes its recipient flat on `shipping` instead (recipientName, addressLine1, ...). An edit on Order Detail
 * merges a partial `shippingAddress` over either, so a stored field always wins and the flat recipient only fills what
 * is missing. Every screen that shows the recipient reads it here, so no screen reads a different field (SSD-7512).
 *
 * A marketing recipient's email sits on the order's customer, where the carrier also reads it when the address carries
 * none; a web order, which has no flat recipient, comes back exactly as stored. Null when the order carries neither.
 */
export const shippingAddressOf = (shipping: unknown, customer?: unknown): Record<string, unknown> | null => {
  const blob = asRecord(shipping);
  const stored = asRecord(blob?.['shippingAddress']);
  const flat: Record<string, unknown> = {
    firstName: blob?.['recipientName'],
    // The import names its recipient in one field; the carrier joins first and last name.
    lastName: blob?.['recipientName'] === undefined ? undefined : '',
    address1: blob?.['addressLine1'],
    address2: blob?.['addressLine2'],
    city: blob?.['city'],
    state: blob?.['state'],
    postcode: blob?.['postcode'],
    country: blob?.['countryCode'],
    phone: blob?.['recipientPhone'],
  };
  const present = Object.fromEntries(Object.entries(flat).filter(([, value]) => typeof value === 'string'));
  if (Object.keys(present).length === 0) {
    return stored;
  }
  // The API nests the person one level down on web orders; the marketing import writes it at the top.
  const person = asRecord(asRecord(customer)?.['customer']) ?? asRecord(customer);
  const email = person?.['email'];
  return { ...present, ...(typeof email === 'string' && email.trim() !== '' ? { email } : {}), ...(stored ?? {}) };
};
